export interface LookupBookResult {
  isbn13: string
  title: string
  authors: string[]
  coverUrl: string | null
  rawPayload: unknown
}

interface OpenLibraryAuthor {
  name?: string
}

interface OpenLibraryBook {
  title?: string
  authors?: OpenLibraryAuthor[]
  cover?: {
    medium?: string
    large?: string
    small?: string
  }
}

interface OpenLibrarySearchDoc {
  title?: string
  author_name?: string[]
  isbn?: string[]
  cover_i?: number
}

interface OpenLibrarySearchResponse {
  docs: OpenLibrarySearchDoc[]
}

const normalizeIsbn = (value: string) => value.replace(/[^0-9Xx]/g, '').toUpperCase()

export async function lookupBookByIsbn(rawBarcode: string): Promise<LookupBookResult> {
  const isbn = normalizeIsbn(rawBarcode)

  if (isbn.length !== 10 && isbn.length !== 13) {
    throw new Error('Barcode must be a valid ISBN-10 or ISBN-13 value.')
  }

  const response = await fetch(
    `https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&jscmd=data&format=json`,
  )

  if (!response.ok) {
    throw new Error('Book lookup failed. Please try again.')
  }

  const payload = (await response.json()) as Record<string, OpenLibraryBook>
  const key = `ISBN:${isbn}`
  const book = payload[key]

  if (!book?.title) {
    throw new Error('No book found for this barcode.')
  }

  return {
    isbn13: isbn,
    title: book.title,
    authors: (book.authors ?? []).map((author) => author.name).filter(Boolean) as string[],
    coverUrl: book.cover?.medium ?? book.cover?.large ?? book.cover?.small ?? null,
    rawPayload: payload,
  }
}

export async function searchBooks(query: string): Promise<LookupBookResult[]> {
  const params = new URLSearchParams({
    q: query,
    limit: '10',
    fields: 'title,author_name,isbn,cover_i',
  })

  const response = await fetch(`https://openlibrary.org/search.json?${params}`)

  if (!response.ok) {
    throw new Error('Search failed. Please try again.')
  }

  const data = (await response.json()) as OpenLibrarySearchResponse

  return data.docs
    .filter((doc) => doc.title && doc.isbn?.length)
    .map((doc) => {
      const isbn13 = doc.isbn?.find(
        (isbn) => isbn.length === 13 && (isbn.startsWith('978') || isbn.startsWith('979')),
      )
      const isbn = isbn13 ?? doc.isbn?.[0] ?? ''
      const coverUrl = doc.cover_i
        ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-S.jpg`
        : null

      return {
        isbn13: isbn,
        title: doc.title ?? '',
        authors: doc.author_name ?? [],
        coverUrl,
        rawPayload: doc,
      }
    })
    .filter((result) => result.isbn13.length > 0)
    .slice(0, 7)
}
